import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";

const PLANS = [
  { value: "free", label: "Free" },
  { value: "pro", label: "Pro" },
  { value: "enterprise", label: "Enterprise" },
];

const COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Germany",
  "France",
  "India",
  "Australia",
  "Singapore",
  "Brazil",
  "Mexico",
];

const TIMEZONES = [
  "UTC−08:00 Pacific Time (US & Canada)",
  "UTC−05:00 Eastern Time (US & Canada)",
  "UTC±00:00 UTC / GMT",
  "UTC+01:00 Central European Time",
  "UTC+05:30 India Standard Time",
  "UTC+08:00 China / Singapore",
  "UTC+10:00 Australia Eastern Time",
];

export default function AccountInfo() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [err, setErr] = useState("");

  // auth/user
  const [userId, setUserId] = useState(null);
  const [email, setEmail] = useState("");
  const [dateJoined, setDateJoined] = useState("");

  // creator_profiles (name + time_zone)
  const [name, setName] = useState("");
  const [nameLocked, setNameLocked] = useState(true);
  const [timeZone, setTimeZone] = useState("");

  // account_info (PII/settings)
  const [organization, setOrganization] = useState("");
  const [phone, setPhone] = useState("");
  const [plan, setPlan] = useState("free");
  const [isComplete, setIsComplete] = useState(false);

  // home address
  const [homeLine1, setHomeLine1] = useState("");
  const [homeLine2, setHomeLine2] = useState("");
  const [homeCity, setHomeCity] = useState("");
  const [homeState, setHomeState] = useState("");
  const [homePostal, setHomePostal] = useState("");
  const [homeCountry, setHomeCountry] = useState("United States");

  // mailing address
  const [mailingSame, setMailingSame] = useState(true);
  const [mailLine1, setMailLine1] = useState("");
  const [mailLine2, setMailLine2] = useState("");
  const [mailCity, setMailCity] = useState("");
  const [mailState, setMailState] = useState("");
  const [mailPostal, setMailPostal] = useState("");
  const [mailCountry, setMailCountry] = useState("United States");

  // ------------------ Load data ------------------
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      setSuccess("");

      const { data: sessionData, error: sessErr } = await supabase.auth.getUser();
      if (sessErr) {
        setErr(sessErr.message || "Unable to read user session");
        setLoading(false);
        return;
      }
      const authUser = sessionData?.user;
      const _userId = authUser?.id || null;
      setUserId(_userId);
      setEmail(authUser?.email || "");
      setDateJoined(
        authUser?.created_at ? new Date(authUser.created_at).toLocaleDateString() : ""
      );

      if (!_userId) {
        setErr("No authenticated user.");
        setLoading(false);
        return;
      }

      const { data: prof, error: pErr } = await supabase
        .from("creator_profiles")
        .select("name, time_zone")
        .eq("user_id", _userId)
        .maybeSingle();

      if (!pErr && prof) {
        const savedName = prof.name || "";
        setName(savedName);
        // Lock is based only on the value already persisted in creator_profiles.
        // Typing into an empty field must never lock it before a successful save.
        setNameLocked(savedName.trim().length > 0);
        setTimeZone(prof.time_zone || "");
      } else if (!pErr) {
        setNameLocked(false);
      }

      const { data: acc, error: aErr } = await supabase
        .from("account_info")
        .select("*")
        .eq("user_id", _userId)
        .maybeSingle();

      if (aErr && aErr.code !== "PGRST116") {
        setErr(aErr.message || "Failed to load account info");
        setLoading(false);
        return;
      }

      if (acc) {
        setOrganization(acc.organization || "");
        setPhone(acc.phone || "");
        setPlan("free");
        setIsComplete(!!acc.is_complete);

        const home = acc.home_address || {};
        setHomeLine1(home.line1 || "");
        setHomeLine2(home.line2 || "");
        setHomeCity(home.city || "");
        setHomeState(home.state || "");
        setHomePostal(home.postal_code || "");
        setHomeCountry(home.country || "United States");

        setMailingSame(acc.mailing_same_as_home ?? true);

        const m = acc.mailing_address || {};
        setMailLine1(m.line1 || "");
        setMailLine2(m.line2 || "");
        setMailCity(m.city || "");
        setMailState(m.state || "");
        setMailPostal(m.postal_code || "");
        setMailCountry(m.country || "United States");
      } else {
        setPlan("free");
        setMailingSame(true);
        setIsComplete(false);
      }

      setLoading(false);
    })();
  }, []);

  // ------------------ Validation ------------------
  const requiredOk = useMemo(() => {
    const hasName = (name || "").trim().length > 1;
    const hasPhone = (phone || "").replace(/\D/g, "").length >= 7;

    const hasHome =
      (homeLine1 || "").trim().length > 2 &&
      (homeCity || "").trim().length > 1 &&
      (homeCountry || "").trim().length > 1;

    let hasMail = true;
    if (!mailingSame) {
      hasMail =
        (mailLine1 || "").trim().length > 2 &&
        (mailCity || "").trim().length > 1 &&
        (mailCountry || "").trim().length > 1;
    }

    const hasTZ = (timeZone || "").trim().length > 0;
    return hasName && hasPhone && hasHome && hasMail && hasTZ;
  }, [
    name,
    phone,
    homeLine1,
    homeCity,
    homeCountry,
    mailingSame,
    mailLine1,
    mailCity,
    mailCountry,
    timeZone,
  ]);

  // ------------------ Save ------------------
  const handleSave = async () => {
    setErr("");
    setSuccess("");
    if (!userId) {
      setErr("No authenticated user.");
      return;
    }
    if (!requiredOk) {
      setErr("Please complete all required fields.");
      return;
    }

    setSaving(true);
    try {
      const profileUpdates = { time_zone: timeZone };
      if (!nameLocked) {
        profileUpdates.name = name.trim();
      }

      const { error: pErr } = await supabase
        .from("creator_profiles")
        .upsert(
          {
            user_id: userId,
            ...profileUpdates,
          },
          {
            onConflict: "user_id",
          }
        );

      if (pErr) {
        throw new Error(pErr.message || "Failed to save profile");
      }

      const home_address = {
        line1: homeLine1,
        line2: homeLine2,
        city: homeCity,
        state: homeState,
        postal_code: homePostal,
        country: homeCountry,
      };
      const mailing_address = mailingSame
        ? null
        : {
            line1: mailLine1,
            line2: mailLine2,
            city: mailCity,
            state: mailState,
            postal_code: mailPostal,
            country: mailCountry,
          };

      const { error: aErr } = await supabase
        .from("account_info")
        .upsert({
          user_id: userId,
          organization,
          phone,
          home_address,
          mailing_same_as_home: mailingSame,
          mailing_address,
          plan,
          is_complete: true,
          completed_at: new Date().toISOString(),
        });
      if (aErr) throw new Error(aErr.message || "Failed to save account info");

      // Only lock the legal name after the complete save succeeds.
      // If any save step fails, the field remains editable for this session.
      if (!nameLocked) {
        setName(name.trim());
        setNameLocked(true);
      }
      setIsComplete(true);
      setSuccess("Account information saved.");

      try {
        localStorage.setItem("df_accountinfo_dismissed", "1");
      } catch (_) {
        // ignore storage errors
      }
      window.dispatchEvent(new CustomEvent("df-accountinfo-complete"));
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ------------------ Deactivate ------------------
  // ------------------ Render ------------------
  return (
    <div className="mx-auto max-w-5xl px-4 py-6 text-slate-100">
      <button
        type="button"
        onClick={() => navigate("/dashboard/settings")}
        className="mb-4 inline-flex items-center gap-1 text-sm text-cyan-300 underline decoration-cyan-400/70 underline-offset-4 hover:text-cyan-200"
      >
        ← Back to Settings
      </button>

      <h1 className="text-xl font-semibold text-sky-200 md:text-2xl">Account Info</h1>
      <p className="mt-1 text-xs text-slate-400 md:text-sm">
        Manage your profile, contact information, plan, and account controls.
      </p>

      <div className="mt-5 rounded-2xl border border-white/10 bg-slate-900/95 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-100">Profile &amp; Identity</div>
          <span
            className={`rounded-full px-3 py-1 text-xs ${
              isComplete
                ? "bg-cyan-500/15 text-cyan-200"
                : "bg-slate-800 text-slate-300"
            }`}
          >
            {isComplete ? "Complete" : "Incomplete"}
          </span>
        </div>

        <div className="mt-4 grid gap-6 md:grid-cols-2">
          <div className="space-y-3 text-sm">
            <div>
              <label className="mb-1 block text-xs font-semibold text-sky-300">
                Legal Full Name <span className="text-rose-400">*</span>{" "}
                {nameLocked && (
                  <span className="text-[11px] font-normal text-slate-400">
                    (read-only)
                  </span>
                )}
              </label>
              <input
                className={`w-full rounded-xl border px-3 py-2 text-sm ${
                  nameLocked
                    ? "border-slate-700 bg-slate-950/40 text-slate-400"
                    : "border-slate-600 bg-slate-950/70 text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                }`}
                value={name}
                onChange={(e) => {
                  if (!nameLocked) setName(e.target.value);
                }}
                disabled={nameLocked}
                placeholder={nameLocked ? "" : "Enter your legal first and last name"}
              />
              <p className="mt-1 text-[11px] text-slate-400">
                {nameLocked
                  ? "Your legal name is saved and is used in invoices and collaboration documents."
                  : "Enter only your legal first and last name. After it is successfully saved, it becomes read-only and is used for invoices and collaboration documents."}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-sky-300">
                Email (login){" "}
                <span className="text-[11px] font-normal text-slate-400">(read-only)</span>
              </label>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-950/40 px-3 py-2 text-sm text-slate-400"
                value={email}
                disabled
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-sky-300">
                Phone Number <span className="text-rose-400">*</span>
              </label>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 415 555 0123"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Digits only are required; formatting is optional.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-sky-300">
                Time Zone <span className="text-rose-400">*</span>
              </label>
              <select
                className="w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
              >
                <option value="">Select your time zone</option>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <label className="mb-1 block text-xs font-semibold text-sky-300">
                Organization / Studio (optional)
              </label>
              <input
                className="w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                placeholder="Dreamframe Studio"
              />
            </div>

            <div className="mt-2 text-sm font-semibold text-slate-100">
              Home Address <span className="text-rose-400">*</span>
            </div>
            <input
              className="mt-1 w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
              value={homeLine1}
              onChange={(e) => setHomeLine1(e.target.value)}
              placeholder="Address line 1"
            />
            <input
              className="mt-2 w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
              value={homeLine2}
              onChange={(e) => setHomeLine2(e.target.value)}
              placeholder="Address line 2 (optional)"
            />
            <div className="mt-2 grid gap-2 md:grid-cols-3">
              <input
                className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                value={homeCity}
                onChange={(e) => setHomeCity(e.target.value)}
                placeholder="City"
              />
              <input
                className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                value={homeState}
                onChange={(e) => setHomeState(e.target.value)}
                placeholder="State / Region"
              />
              <input
                className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                value={homePostal}
                onChange={(e) => setHomePostal(e.target.value)}
                placeholder="Postal Code"
              />
            </div>
            <select
              className="mt-2 w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
              value={homeCountry}
              onChange={(e) => setHomeCountry(e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <div className="mt-3">
              <label className="mb-1 block text-xs font-semibold text-sky-300">
                Mailing Address same as Home?
              </label>
              <select
                className="w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
                value={mailingSame ? "yes" : "no"}
                onChange={(e) => setMailingSame(e.target.value === "yes")}
              >
                <option value="yes">Yes (use Home Address)</option>
                <option value="no">No (enter mailing address)</option>
              </select>
            </div>

            {!mailingSame && (
              <>
                <div className="mt-3 text-sm font-semibold text-slate-100">
                  Mailing Address <span className="text-rose-400">*</span>
                </div>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                  value={mailLine1}
                  onChange={(e) => setMailLine1(e.target.value)}
                  placeholder="Address line 1"
                />
                <input
                  className="mt-2 w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                  value={mailLine2}
                  onChange={(e) => setMailLine2(e.target.value)}
                  placeholder="Address line 2 (optional)"
                />
                <div className="mt-2 grid gap-2 md:grid-cols-3">
                  <input
                    className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                    value={mailCity}
                    onChange={(e) => setMailCity(e.target.value)}
                    placeholder="City"
                  />
                  <input
                    className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                    value={mailState}
                    onChange={(e) => setMailState(e.target.value)}
                    placeholder="State / Region"
                  />
                  <input
                    className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
                    value={mailPostal}
                    onChange={(e) => setMailPostal(e.target.value)}
                    placeholder="Postal Code"
                  />
                </div>
                <select
                  className="mt-2 w-full rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
                  value={mailCountry}
                  onChange={(e) => setMailCountry(e.target.value)}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>

        {err && <div className="mt-3 text-sm text-rose-400">{err}</div>}
        {success && <div className="mt-3 text-sm text-emerald-400">{success}</div>}
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center rounded-xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-fuchsia-500/40 hover:brightness-110 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Account Info"}
          </button>
          <span className={`text-xs ${requiredOk ? "text-emerald-300" : "text-rose-300"}`}>
            {requiredOk ? "Ready to save" : "Fill all * required fields to complete"}
          </span>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-white/10 bg-slate-900/95 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
        <div className="text-sm font-semibold text-slate-100">Account Details</div>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-white/10 bg-slate-800/70 p-3">
            <div className="text-[11px] text-slate-400">Plan / Subscription</div>
            <select
              className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950/70 px-2.5 py-1.5 text-xs text-slate-100 focus:border-cyan-400 focus:outline-none"
              value={plan}
              disabled
            >
              {PLANS.filter((p) => p.value === "free").map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="rounded-xl border border-white/10 bg-slate-800/70 p-3">
            <div className="text-[11px] text-slate-400">Date Joined</div>
            <div className="text-xs text-slate-100">{dateJoined || "—"}</div>
          </div>

          <div className="rounded-xl border border-white/10 bg-slate-800/70 p-3">
            <div className="text-[11px] text-slate-400">Account ID</div>
            <div className="break-all text-xs text-slate-100">{userId || "—"}</div>
          </div>
        </div>
      </div>

      {/* Account Actions
      <div className="mt-5 rounded-2xl border border-white/10 bg-slate-900/95 p-4 shadow-lg shadow-fuchsia-500/10 backdrop-blur">
        <div className="text-sm font-semibold text-slate-100">Account Actions</div>
        <p className="mt-2 text-xs text-slate-200">
          Deactivating will <span className="font-semibold">freeze projects and escrow funds</span>.
          You can reactivate within 30 days before permanent deletion. Regulatory records may be retained as required.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
          <button
            type="button"
            onClick={handleDeactivate}
            className="inline-flex items-center rounded-xl border border-rose-400/60 bg-transparent px-4 py-2 text-sm font-semibold text-rose-300 hover:bg-rose-500/10"
          >
            Deactivate Account
          </button>
        </div>
      </div>*/}

      {loading && (
        <div className="mt-3 text-xs text-slate-400">Loading account data…</div>
      )}
    </div>
  );
}
